import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useStudioPlan } from '../hooks/useStudioPlan';

interface AttendanceRecord {
  id: string;
  attended_at: string;
  class_id: string | null;
  event_id: string | null;
  notes: string | null;
  class_name?: string | null;
  event_name?: string | null;
  display_name?: string;
}

interface AttendanceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
}

interface GroupedAttendance {
  [monthYear: string]: AttendanceRecord[];
}

const AttendanceHistoryModal: React.FC<AttendanceHistoryModalProps> = ({
  isOpen,
  onClose,
  studentId,
  studentName,
}) => {
  const { getAuthToken } = useAuth();
  const { plan } = useStudioPlan();
  const historyDays = plan?.capabilities.attendanceHistoryDays ?? null;
  const isHistoryCapped = historyDays != null && historyDays > 0;
  const [loading, setLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchAttendanceHistory();
    }
  }, [isOpen, studentId]);

  const fetchAttendanceHistory = async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      if (!baseUrl) {
        throw new Error('Backend URL not configured');
      }

      const response = await fetch(
        `${baseUrl}/api/supabase/students/${studentId}/attendance-history`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to load attendance history (${response.status})`);
      }

      const data: AttendanceRecord[] = await response.json();
      setAttendanceRecords(data);
    } catch (err: any) {
      console.error('Error fetching attendance history:', err);
      setError(err.message || 'Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  };

  const formatDateToLocal = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const month = date.getMonth() + 1; // 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    
    return {
      date: `${month}/${day}/${year}`,
      time: `${hours}:${minutesStr} ${ampm}`,
    };
  };

  const groupByMonth = (records: AttendanceRecord[]): GroupedAttendance => {
    const grouped: GroupedAttendance = {};
    
    records.forEach((record) => {
      const date = new Date(record.attended_at);
      const monthYear = date.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
      
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(record);
    });
    
    return grouped;
  };

  const groupedAttendance = groupByMonth(attendanceRecords);
  const monthYears = Object.keys(groupedAttendance);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl max-h-[90vh] flex flex-col transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between mb-4 flex-shrink-0">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                      Attendance History
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {studentName}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {isHistoryCapped && (
                  <div className="mt-4 flex-shrink-0 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                    Showing attendance from the last {historyDays} days.{' '}
                    <Link
                      to="/app/settings/billing"
                      className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                    >
                      Upgrade
                    </Link>{' '}
                    for full history.
                  </div>
                )}

                <div className="mt-4 overflow-y-auto flex-1 pr-2">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : error ? (
                    <div className="text-red-600 dark:text-red-400 text-center py-8">
                      {error}
                    </div>
                  ) : attendanceRecords.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                      {isHistoryCapped
                        ? `No attendance records found in the last ${historyDays} days.`
                        : 'No attendance records found.'}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {monthYears.map((monthYear) => {
                        const monthRecords = groupedAttendance[monthYear];
                        const classCount = monthRecords.filter(r => r.class_name).length;
                        const eventCount = monthRecords.filter(r => r.event_name).length;
                        const totalCount = monthRecords.length;
                        
                        return (
                          <div key={monthYear}>
                            <div className="mb-3">
                              <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center">
                                <ClockIcon className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                                {monthYear}
                              </h4>
                              <div className="mt-2 flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <span className="font-medium text-purple-600 dark:text-purple-400">{classCount}</span> Classes
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="font-medium text-blue-600 dark:text-blue-400">{eventCount}</span> Events
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="font-medium text-gray-900 dark:text-white">{totalCount}</span> Total
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {groupedAttendance[monthYear].map((record) => {
                                const { date, time } = formatDateToLocal(record.attended_at);
                                return (
                                  <div
                                    key={record.id}
                                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            {date}
                                          </span>
                                          <span className="text-sm text-gray-500 dark:text-gray-400">
                                            at {time}
                                          </span>
                                        </div>
                                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                          {record.class_name ? `Class: ${record.class_name}` : record.event_name ? `Event: ${record.event_name}` : 'No class/event'}
                                        </div>
                                        {record.notes && (
                                          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
                                            Note: {record.notes}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AttendanceHistoryModal;
